import type {
  SolarHdmProjectType,
  SolarHdmQuoteDraftDto,
  SolarHdmSurveyLanguage,
} from "~/api/dto/solar-proposal-hdm.dto"

/** The draft's numeric fields, edited as text. */
export const HDM_NUMBER_FIELDS = [
  `annualUsageKwh`,
  `systemSizeKw`,
  `year1ProductionKwh`,
  `initialTermKwh`,
  `sowPrice`,
  `sowPerW`,
  `solarPrepayAmount`,
  `batteryKwSize`,
  `batteryCostPerKw`,
  `batterySalesPrice`,
  `batteryPrepaid`,
  `grandTotalPrice`,
  `grandTotalPrepay`,
] as const satisfies readonly (keyof SolarHdmQuoteDraftDto)[]

export type SolarHdmNumberField = (typeof HDM_NUMBER_FIELDS)[number]

export const HDM_TEXT_FIELDS = [
  `customerFirstName`,
  `customerLastName`,
  `email`,
  `phoneNumber`,
  `street`,
  `city`,
  `zip`,
] as const satisfies readonly (keyof SolarHdmQuoteDraftDto)[]

export type SolarHdmTextField = (typeof HDM_TEXT_FIELDS)[number]

/** The form: the draft with every number as the text the seller typed. */
export type SolarHdmForm = Omit<SolarHdmQuoteDraftDto, SolarHdmNumberField> &
  Record<SolarHdmNumberField, string>

export type SolarHdmFormErrors = Partial<Record<keyof SolarHdmForm, string>>

export const WITH_STORAGE: SolarHdmProjectType = `Residential Solar with Storage`

export const HDM_SURVEY_LANGUAGES: readonly SolarHdmSurveyLanguage[] = [
  `English`,
  `Spanish`,
]

export const toSolarHdmForm = (draft: SolarHdmQuoteDraftDto): SolarHdmForm => {
  const form = { ...draft } as unknown as SolarHdmForm
  for (const field of HDM_NUMBER_FIELDS) {
    const value = draft[field]
    form[field] = value == null ? `` : String(value)
  }
  return form
}

const REQUIRED_TEXT: Record<SolarHdmTextField, string> = {
  customerFirstName: `Enter the homeowner's first name`,
  customerLastName: `Enter the homeowner's last name`,
  email: `Enter the homeowner's email`,
  phoneNumber: `Enter the homeowner's phone number`,
  street: `Enter the street address`,
  city: `Enter the city`,
  zip: `Enter the ZIP code`,
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** An optional leading `$`, then digits grouped by thousands (`1,234`) or not, then decimals. */
const AMOUNT = /^\$?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/

/**
 * `"$1,234.5"` → 1234.5; empty → null; anything else → undefined (invalid).
 * A misplaced separator (`1,2`, `1 00`) is an error, never silently dropped.
 */
const parseNumber = (text: string): number | null | undefined => {
  const trimmed = text.trim()
  if (trimmed === ``) return null
  if (!AMOUNT.test(trimmed)) return undefined
  const value = Number(trimmed.replace(/[$,]/g, ``))
  return Number.isFinite(value) ? value : undefined
}

const formatAmount = (value: number): string =>
  String(Math.round(value * 100) / 100)

/**
 * The form with another project type. The grand totals are the solar price
 * plus the battery's, as the draft prefills them, so they follow the type:
 * dropping storage drops the battery from both totals, adding it back adds it.
 * Totals stay as typed when a part they add up from isn't a number yet.
 */
export const withProjectType = (
  form: SolarHdmForm,
  projectType: SolarHdmProjectType,
): SolarHdmForm => {
  if (projectType === form.projectType) return form
  const next = { ...form, projectType }
  const storage = projectType === WITH_STORAGE
  const sum = (solar: string, battery: string): string | null => {
    const solarValue = parseNumber(solar)
    const batteryValue = storage ? parseNumber(battery) : 0
    return solarValue == null || batteryValue == null
      ? null
      : formatAmount(solarValue + batteryValue)
  }
  const total = sum(form.sowPrice, form.batterySalesPrice)
  const prepay = sum(form.solarPrepayAmount, form.batteryPrepaid)
  return {
    ...next,
    grandTotalPrice: total ?? form.grandTotalPrice,
    grandTotalPrepay: prepay ?? form.grandTotalPrepay,
  }
}

/**
 * The draft HDM receives, or what the seller has to fix first. Battery fields
 * are checked only for a solar-with-storage quote; the gateway drops them
 * otherwise.
 */
export const solarHdmDraftFromForm = (
  form: SolarHdmForm,
):
  | { draft: null; errors: SolarHdmFormErrors }
  | { draft: SolarHdmQuoteDraftDto; errors: null } => {
  const errors: SolarHdmFormErrors = {}
  const numbers = {} as Record<SolarHdmNumberField, number | null>
  const storage = form.projectType === WITH_STORAGE

  for (const field of HDM_TEXT_FIELDS) {
    if (form[field].trim() === ``) errors[field] = REQUIRED_TEXT[field]
  }
  if (!errors.email && !EMAIL.test(form.email.trim())) {
    errors.email = `Enter a valid email`
  }
  if (!form.utilityId) errors.utilityId = `Choose the homeowner's utility`

  for (const field of HDM_NUMBER_FIELDS) {
    const value = parseNumber(form[field])
    if (value === undefined) {
      if (storage || !field.startsWith(`battery`)) {
        errors[field] = `Enter a number of zero or more`
      }
      numbers[field] = null
    } else {
      numbers[field] = value
    }
  }
  if (
    !errors.annualUsageKwh &&
    !(numbers.annualUsageKwh && numbers.annualUsageKwh >= 1)
  ) {
    errors.annualUsageKwh = `Enter the annual usage`
  }

  if (Object.keys(errors).length > 0) return { draft: null, errors }

  const trimmed = Object.fromEntries(
    HDM_TEXT_FIELDS.map((field) => [field, form[field].trim()]),
  ) as Record<SolarHdmTextField, string>

  return {
    draft: { ...form, ...trimmed, ...numbers },
    errors: null,
  }
}
